import { initialMapBounds, maximumMapBounds } from '../domain/camera.ts';
import {
  resolvePublicRuntimeConfig,
  safeRuntimeConfig,
  type PublicRuntimeConfig,
} from '../config/runtime-config.ts';
import { localMapContentPaths } from '../config/map-content.ts';
import { parseGeoJsonFeatureCollection } from '../domain/geojson.ts';
import { layerDefinitions, type MapContentSourceId } from '../domain/layers.ts';
import {
  createTrailDataVersion,
  normalizeSelectableTrails,
  toCanonicalTrailCollection,
} from '../domain/trails.ts';
import type { GeoJsonFeatureCollection, MapMountOptions } from '../map/contracts.ts';

async function fetchJson(path: string): Promise<unknown> {
  const response = await fetch(new URL(path, document.baseURI));
  if (!response.ok)
    throw new Error(`application data request failed with ${String(response.status)}`);
  return response.json() as Promise<unknown>;
}

function manifestTrailDataVersion(value: unknown): ReturnType<typeof createTrailDataVersion> {
  if (typeof value !== 'object' || value === null || !('trailDataVersion' in value)) {
    throw new TypeError('trail data manifest is invalid');
  }
  return createTrailDataVersion(value.trailDataVersion);
}

export async function loadRuntimeConfiguration(
  loader: (path: string) => Promise<unknown> = fetchJson,
): Promise<PublicRuntimeConfig> {
  const trailDataVersion = manifestTrailDataVersion(await loader('./data/manifest.json'));
  try {
    const runtime = await loader('./config/runtime-config.json');
    return resolvePublicRuntimeConfig(runtime, trailDataVersion).config;
  } catch {
    return safeRuntimeConfig(trailDataVersion);
  }
}

export async function loadTrailDataVersion(
  loader: (path: string) => Promise<unknown> = fetchJson,
): Promise<ReturnType<typeof createTrailDataVersion>> {
  return (await loadRuntimeConfiguration(loader)).trailDataVersion;
}

export async function loadMapLibreMountOptions(): Promise<MapMountOptions> {
  const localSourceEntries = Object.entries(localMapContentPaths);
  const [runtimeConfig, trailSource, loadedLocalSources] = await Promise.all([
    loadRuntimeConfiguration(),
    fetchJson('./data/trails.geojson'),
    Promise.all(
      localSourceEntries.map(async ([sourceId, path]) =>
        Object.freeze({ sourceId, value: await fetchJson(`../${path}`) }),
      ),
    ),
  ]);
  const selectableTrails = normalizeSelectableTrails({
    type: 'FeatureCollection',
    features: parseGeoJsonFeatureCollection(trailSource, 'canonical trails').features.map(
      (feature) => {
        const candidate = feature as {
          readonly geometry: unknown;
          readonly properties: Readonly<Record<string, unknown>>;
        };
        return {
          type: 'Feature',
          geometry: candidate.geometry,
          properties: {
            activitytype: 0,
            trailid: candidate.properties['trailId'],
            title: candidate.properties['title'],
            difficulty: candidate.properties['difficulty'],
            trailforksurl: candidate.properties['externalUrl'],
          },
        };
      },
    ),
  });
  const sourceData: Partial<Record<MapContentSourceId, GeoJsonFeatureCollection>> = {
    'canonical-trails': toCanonicalTrailCollection(selectableTrails),
  };
  loadedLocalSources.forEach(({ sourceId, value }) => {
    sourceData[sourceId as keyof typeof localMapContentPaths] = parseGeoJsonFeatureCollection(
      value,
      sourceId,
    );
  });
  return Object.freeze({
    locale: 'de',
    initialView: { type: 'bounds', bounds: initialMapBounds, maximumZoom: 12 },
    maximumBounds: maximumMapBounds,
    content: Object.freeze({
      trailDataVersion: runtimeConfig.trailDataVersion,
      selectableTrails,
      layers: layerDefinitions,
      sourceData: Object.freeze(sourceData),
    }),
  } satisfies MapMountOptions);
}
